<template>
  <RcModal
    :id="modalId"
    size="md"
    contentClass="rc-modal-form rc-modal-segment"
    hideFooter
    @keyup="onKeyup"
    @show="onShow"
    @shown="onShown"
    @hide="onHide"
    @init="modalInstance = $event"
  >
    <template #modal-title>
      {{ modalTitle }}
    </template>

    <template #modal-body="{ cancel }">
      <!-- step 1 : select range  -->
      <template v-if="currentStep == 1">
        <!--Accessibility key/value-->
        <div class="rc-media-accessibility">
          <rc-btn
            v-if="isPlaying"
            variant="primary"
            size="lg"
            @click="player.pause()"
          >
            Pause
          </rc-btn>
          <rc-btn
            v-else
            variant="primary"
            size="lg"
            @click="playerPlay()"
          >
            {{
              $t("catalog.play")
            }}
          </rc-btn>
        </div>
        <div class="rc-segment-media">
          <div
            id="rcPlayerSegment"
            class="ratio ratio-16x9"
          >
            <!-- videojs player -->
            <RcVideojsPlayer
              :options="videoOptions"
              @on-player-ready="onPlayerReady"
              @play="onPlaybackStart"
              @pause="onPlaybackPause"
              @ended="onPlaybackEnded"
              @timeupdate="onTimeUpdate"
            />
          </div>
        </div>
        <div class="rc-segment-slider">
          <div class="row justify-content-between align-items-center g-0">
            <div class="col">
              <label
                for="rcSegmentStart"
                class="form-label"
              >{{
                $t("catalog.segment.label_start")
              }}</label>

              <input
                id="rcSegmentStart"
                type="text"
                disabled="disabled"
                readonly="readonly"
                class="form-control"
                :value="useShortDuration(range[0])"
              >
            </div>
            <div class="col d-none d-sm-block">
              <div class="rc-horizontal-divider" />
            </div>
            <div class="col text-center">
              <span class="visually-hidden">{{ $t("common.duration") }} :
                {{ useLocaleDuration(range[1] - range[0], $i18n.locale) }}</span>

              <div
                class="rc-duration"
                aria-hidden="true"
              >
                {{ useShortDuration(range[1] - range[0]) }}
              </div>
            </div>
            <div class="col d-none d-sm-block">
              <div class="rc-horizontal-divider" />
            </div>
            <div class="col">
              <label
                for="rcSegmentEnd"
                class="form-label"
              >{{
                $t("catalog.segment.label_end")
              }}</label>

              <input
                id="rcSegmentEnd"
                type="text"
                disabled="disabled"
                readonly="readonly"
                class="form-control"
                :value="useShortDuration(range[1])"
              >
            </div>
          </div>

          <Slider
            v-model="range"
            :max="item.duration"
            :enable-cross="false"
            :format="formatTooltip"
            :merge="9999"
            showTooltip="drag"
            dotSize="28"
            height="8px"
            class="rc-slider mt-4 mb-3"
            @change="onSliderChange"
          />
          <p class="my-4">
            <small class="text-secondary">
              {{ $t("catalog.segment.help") }}
            </small>
          </p>
          <div
            class="d-flex justify-content-between align-items-center mb-4 border-top pt-3"
          >
            <button
              class="btn btn-light btn-md d-block"
              @click="cancel()"
            >
              {{ $t("common.cancel") }}
            </button>
            <button
              class="btn btn-primary btn-lg d-block"
              @click="onContinue"
            >
              {{ $t("common.continue") }}
            </button>
          </div>
        </div>
      </template>

      <!-- step 2 : choose your destination  -->
      <template v-else>
        <div class="rc-segment-media">
          <div class="rc-card-unit">
            <div class="rc-thumbnail-wrapper">
              <RcImgLazy
                :src="
                  item.image_versions ? item.image_versions.medium : item.image
                "
                alt=""
                fluid-grow
                @error="imageUrlFallback"
              />
            </div>
            <div class="rc-card-body">
              <div class="rc-card-text">
                <span class="rc-duration">
                  <span aria-hidden="true">{{
                    useShortDuration(range[1] - range[0])
                  }}</span>
                  <span class="visually-hidden">{{
                    useLocaleDuration(range[1] - range[0], $i18n.locale)
                  }}</span>
                </span>
                {{ $t("catalog.segment.label_start") }}
                {{ useShortDuration(range[0]) }}
              </div>
            </div>
          </div>
        </div>

        <ul
          id="myTab"
          class="nav nav-tabs rc-nav-tabs-sm"
          role="tablist"
        >
          <li
            class="nav-item"
            role="presentation"
          >
            <button
              id="permalinkTab"
              class="nav-link active"
              data-bs-toggle="tab"
              data-bs-target="#permalinkTabPanel"
              type="button"
              role="tab"
              aria-controls="permalinkTabPanel"
              aria-selected="true"
            >
              <div class="px-2 pb-2">
                <RcIcon
                  name="link"
                  scale="1.1"
                />
                <span class="d-block mt-1">{{ $t("common.permalink") }}</span>
              </div>
            </button>
          </li>

          <li
            class="nav-item"
            role="presentation"
          >
            <button
              id="playlistTab"
              class="nav-link"
              data-bs-toggle="tab"
              data-bs-target="#playlistTabPanel"
              type="button"
              role="tab"
              aria-controls="playlistTabPanel"
              aria-selected="false"
            >
              <div class="px-2 pb-2">
                <RcIcon
                  name="playlistAdd"
                  scale="1.25"
                />
                <span class="visually-hidden">{{
                  $t("catalog.playlist_add_to_only")
                }}</span>
                <span class="d-block mt-1">{{ $t("catalog.playlist") }} </span>
              </div>
            </button>
          </li>
        </ul>
        <div class="tab-content">
          <div
            id="permalinkTabPanel"
            class="tab-pane fade show active"
            role="tabpanel"
            aria-labelledby="permalinkTab"
          >
            <div class="rc-input-btn-wrapper mt-2 mw-100">
              <input
                id="linkInput"
                type="text"
                :value="form.link"
                disabled="disabled"
                class="form-control form-control-lg w-100"
                aria-describedby="linkInput_desc"
              >
              <button
                class="btn btn-primary btn-sm"
                @click="copyLink(form.link)"
              >
                {{ $t("common.copy") }}
              </button>
            </div>
            <small
              id="linkInput_desc"
              class="form-text text-secondary"
            >{{
              permalinkHelpText
            }}</small>
          </div>
          <div
            id="playlistTabPanel"
            class="tab-pane fade"
            role="tabpanel"
            aria-labelledby="playlistTab"
          >
            <!-- add screener to playlist -->
            <template v-if="modal.mode == 'add'">
              <!-- add to existing playlist -->
              <template v-if="showPlaylistOptions">
                <div class="mb-3">
                  <label
                    :for="playlistInputOptions.id"
                    class="form-label"
                  >{{
                    $t("catalog.playlist_select")
                  }}</label>

                  <RcComboBox
                    :inputOptions="playlistInputOptions"
                    :apiListOptions="playlistApiOptions"
                    size="lg"
                    @selected="onPlaylistSelected"
                    @init="onInitPlaylists"
                  />

                  <div
                    v-if="validateState('uid') == false"
                    id="feedback_invalid__playlistUid"
                    aria-live="assertive"
                    aria-atomic="true"
                    class="invalid-feedback d-block"
                  >
                    {{ $t("catalog.playlist_required") }}
                  </div>
                </div>

                <div class="d-flex justify-content-center">
                  <RcDebounced
                    variant="primary"
                    block
                    size="lg"
                    :promise="addToPlaylist.bind()"
                  >
                    {{ $t("common.save") }}
                  </RcDebounced>
                </div>
                <div class="d-flex justify-content-center">
                  <rc-btn
                    v-orphan
                    variant="link"
                    block
                    class="rc-btn-link-secondary mt-3 btn-sm fw-normal"
                    @click="changeModalMode"
                  >
                    {{ $t("catalog.playlist_add_new") }}
                  </rc-btn>
                </div>
              </template>

              <!-- add to new playlist -->
              <template v-else>
                <p class="mt-1">
                  {{ $t("catalog.playlist_name_message") }}
                </p>
                <div class="mb-3">
                  <label
                    for="playlistInput"
                    class="form-label"
                  >{{
                    $t("catalog.playlist_title")
                  }}</label>
                  <input
                    id="playlistInput"
                    v-model="form.name"
                    type="text"
                    class="form-control form-control-lg"
                    :class="validateState('name') != false ? '' : 'is-invalid'"
                    :aria-invalid="validateState('name') != false ? null : true"
                    :aria-describedby="
                      validateState('name') != false
                        ? null
                        : 'feedback_invalid__playlistName'
                    "
                  >

                  <div
                    v-if="validateState('name') == false"
                    id="feedback_invalid__playlistName"
                    aria-live="assertive"
                    aria-atomic="true"
                    class="invalid-feedback d-block"
                  >
                    <div v-if="!v$.form.name.required.$response">
                      {{ $t("catalog.title_required") }}
                    </div>
                    <div v-if="!v$.form.name.maxLength.$response">
                      {{ $t("catalog.title_too_long") }}
                      ({{ v$.form.name.$params.maxLength.max }})
                    </div>
                  </div>
                </div>
                <div class="d-flex justify-content-center">
                  <RcDebounced
                    variant="primary"
                    block
                    size="lg"
                    class="mb-3"
                    :promise="handleOk.bind()"
                  >
                    {{ $t("common.save") }}
                  </RcDebounced>
                </div>
              </template>
            </template>

            <!-- create a playlist -->
            <template v-if="modal.mode == 'create'">
              <div class="mb-3">
                <label
                  for="playlistInput"
                  class="form-label"
                >{{
                  $t("catalog.playlist_title")
                }}</label>

                <input
                  id="playlistInput"
                  v-model="form.name"
                  type="text"
                  class="form-control form-control-lg"
                  :class="validateState('name') != false ? '' : 'is-invalid'"
                  :aria-invalid="validateState('name') != false ? null : true"
                  :aria-describedby="
                    validateState('name') != false
                      ? null
                      : 'feedback_invalid__playlistName'
                  "
                >

                <div
                  v-if="validateState('name') == false"
                  id="feedback_invalid__playlistName"
                  aria-live="assertive"
                  aria-atomic="true"
                  class="invalid-feedback d-block"
                >
                  <div v-if="!v$.form.name.required.$response">
                    {{ $t("catalog.title_required") }}
                  </div>
                  <div v-if="!v$.form.name.maxLength.$response">
                    {{ $t("catalog.title_too_long") }}
                    ({{ v$.form.name.$params.maxLength.max }})
                  </div>
                </div>
              </div>

              <div class="d-flex justify-content-center">
                <RcDebounced
                  variant="primary"
                  block
                  size="lg"
                  :promise="handleOk.bind()"
                >
                  {{ $t("common.save") }}
                </RcDebounced>
              </div>
              <div class="d-flex justify-content-center">
                <rc-btn
                  v-orphan
                  variant="link"
                  block
                  class="rc-btn-link-secondary mt-3 btn-sm fw-normal"
                  @click="changeModalMode"
                >
                  {{ $t("catalog.playlist_existing_select") }}
                </rc-btn>
              </div>
            </template>
          </div>
        </div>
      </template>
    </template>
  </RcModal>
</template>

<script>
import { mapState } from 'vuex';
import RcIcon from '@/libvue/components/icons/Base.vue';
import RcImgLazy from '@/libvue/components/image/ImageLazy.vue';

import RcDebounced from '@/libvue/components/button/DebouncedButton.vue';
import Config from '#/config';

import { playlistsMixin } from '@/libvue/apps/catalog/playlists/mixins';
import { videojsMixin } from '@/libvue/apps/catalog/mixins/videojsMixin';
import { catalogItemMixin } from '@/libvue/apps/catalog/mixins/itemMixin';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';

import RcComboBox from '@/libvue/components/select/comboBox.vue';
import RcVideojsPlayer from '@/libvue/components/player/videojsPlayer.vue';

import {
  useLocaleDuration,
  useShortDuration
} from '@/libvue/composables/durations';
import RcModal from '@/libvue/components/modal/Modal.vue';

import Slider from '@vueform/slider';
import '@vueform/slider/themes/default.css';
export default {
  name: 'RcCreateSegmentModal',

  components: {
    RcIcon,
    RcImgLazy,
    RcDebounced,
    Slider,
    RcVideojsPlayer,
    RcComboBox,
    RcModal
  },

  mixins: [playlistsMixin, videojsMixin, catalogItemMixin],

  props: {
    permalinkHelpText: {
      type: String,
      required: false,
      default: 'test'
    }
    // sources: {
    //   type: Array,
    //   required: true
    // }
  },
  setup() {
    return {
      useLocaleDuration,
      useShortDuration,
      v$: useVuelidate()
    };
  },

  data() {
    return {
      modalId: 'createSegmentModal',
      modalInstance: '',
      showPlaylistOptions: true,
      config: Config,
      currentStep: 1,
      form: {
        name: null,
        link: '',
        uid: null
      },
      formatTooltip: (v) => this.useShortDuration(v)
    };
  },

  validations: {
    form: {
      name: { required, maxLength: maxLength(200) },
      uid: { required }
    }
  },

  computed: {
    ...mapState({
      profile: (state) => state.auth.user
    }),
    targetId() {
      return 'segmentModalTarget';
    },
    modalTitle() {
      return this.$t('catalog.segment.create');
    },
    screenerContentMetas() {
      const metas = [
        this.item.group_item ? `show : ${this.item.group_item.name}` : null,
        this.item.season ? `season : ${this.item.season}` : null,
        this.item.episodes ? `episodes : ${this.item.episodes}` : null,
        this.item.title ? `title : ${this.item.title}` : null
      ];

      return metas.filter(Boolean).join(' | ');
    },
    playlistApiOptions() {
      return {
        url: `users/${this.profile.user_uid}/playlists`,
        method: 'get',
        params: {
          playlist_type: 'manual',
          ordering: 'name'
        }
      };
    },
    playlistInputOptions() {
      return {
        id: 'playlistSelect',
        placeholder: this.$t('catalog.playlist_search'),
        class: this.validateState('uid') != false ? '' : 'is-invalid',
        'aria-invalid': this.validateState('uid') != false ? null : true,
        'aria-describedby':
          this.validateState('uid') != false
            ? null
            : 'feedback_invalid__playlistUid'
      };
    }
  },

  methods: {
    validateState(key) {
      const { $dirty, $error } = this.v$.form[key];
      return $dirty ? !$error : null;
    },
    handleOk() {
      this.v$.form.$touch();

      if (this.v$.form['name'].$error) {
        return;
      }

      const payload = {
        playlist_name: this.form.name,
        screener: this.modal?.item?.screeners[0].uid,
        position: 0,
        segment_in: this.range[0],
        segment_out: this.range[1]
      };

      this.addScreeners(payload, this.form.name).then(() =>
        this.modalInstance.hide()
      );
    },
    addToPlaylist() {
      this.v$.form.$touch();

      if (this.v$.form['uid'].$error) {
        return;
      }
      if (!this.addToPlaylistUid) return;

      const payload = {
        playlist_id: this.addToPlaylistUid,
        screener: this.modal?.item?.screeners[0].uid,
        position: 0,
        segment_in: this.range[0],
        segment_out: this.range[1]
      };

      this.addScreeners(payload, this.form.name).then(() =>
        this.modalInstance.hide()
      );
    },
    onShow() {
      this.initPlayer(this, this.item).then((src) => {
        const itemType = this.item.item
          ? this.item.item.item_type
          : this.item.item_type;

        const type = itemType == 'video' ? 'vod' : 'aod';

        this.videoOptions.sources = [src];
        this.videoOptions.screener = {
          title: this.activeScreener.name,
          uid: this.activeScreener.uid,
          duration: this.activeScreener.duration.toString(),
          type
        };
      });
    },
    onShown() {
      this.range = [0, this.item.duration];

      document
        .getElementsByClassName('slider-handle-upper')[0]
        .setAttribute('aria-label', this.$t('catalog.segment.select_start'));
      document
        .getElementsByClassName('slider-handle-lower')[0]
        .setAttribute('aria-label', this.$t('catalog.segment.select_end'));
    },
    onHide() {
      this.v$.form.$reset();
      this.currentStep = 1;
      if (!this.player.isDisposed_) {
        this.player.pause();
      }
    },
    getSharedLink() {
      let route = this.$router.resolve({
        name: 'catalog.item.detail',
        params: {
          uid: this.$route.params.uid,

          locale: this.$i18n.locale
        },
        query: {
          in: this.range[0],
          out: this.range[1]
        }
      });

      const host = Config.apiHost() ? Config.apiHost() : window.location.origin;
      return `${host}${route.href}`;
    },
    onContinue() {
      this.currentStep = 2;
      this.form.link = this.getSharedLink();
    },
    onPlaylistSelected(selected) {
      if (selected) {
        this.addToPlaylistUid = selected.uid;
        this.form.name = selected.name;
        this.form.uid = selected.uid;
      } else {
        this.addToPlaylistUid = null;
        this.form.name = null;
        this.form.uid = null;
      }
      this.validateState('uid');
    },
    onInitPlaylists(e) {
      this.showPlaylistOptions = Boolean(e.length);
    },
    onPlayerReady(e) {
      this.player = e;
    }
  }
};
</script>
<style lang="scss"></style>
